import { useId, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Send, GitBranch, Link2, Upload } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr, downloadBlob } from "@/lib/api";
import {
  Icon, Button, IconButton, TextField, Select, Tabs, Badge, Avatar, Panel, PageHead, StatCell,
  DecisionLogEntry, Skeleton, useViewport,
} from "@/sentinel";

const STATUSES = ["Backlog", "In progress", "In review", "Blocked", "Done"];
const PRIORITIES = ["P0", "P1", "P2"];
// Priority bars (sn-prio): P0 is the most urgent → three bars.
const PRIO_LEVEL = { P0: 3, P1: 2, P2: 1 };
const STATUS_ICON = { Backlog: "Circle", "In progress": "CircleDot", "In review": "Eye", Blocked: "CircleSlash", Done: "CircleCheck" };
const opts = (list) => list.map((v) => ({ value: v, label: v }));

/** Multi-line field in the TextField style (the library has no textarea). */
function TextArea({ label, rows = 3, ...rest }) {
  const id = useId();
  return (
    <div className="sn-field">
      {label && <label className="sn-field-label" htmlFor={id}>{label}</label>}
      <div className="sn-input-wrap" style={{ height: "auto", alignItems: "stretch" }}>
        <textarea id={id} rows={rows} className="sn-input" style={{ padding: "var(--space-2)", lineHeight: "20px", resize: "vertical" }} {...rest} />
      </div>
    </div>
  );
}

function Comments({ taskId }) {
  const [text, setText] = useState("");
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-comments", taskId], queryFn: () => api.pmComments(taskId) });
  const send = async () => {
    if (!text.trim()) return;
    try { await api.pmAddComment(taskId, text); setText(""); refetch(); } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-hairline)" }} data-testid={`pm-comments-${taskId}`}>
      {rows.map((c) => (
        <div key={c.id} className="sn-stack" style={{ gap: 0 }}>
          <span className="sn-small sn-faint">{c.author}</span>
          <span className="sn-small" style={{ color: "var(--text-primary)" }}>{c.body}</span>
        </div>
      ))}
      <div className="sn-row" style={{ flexWrap: "nowrap", gap: "var(--space-1)" }}>
        <TextField value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Comment…" aria-label="Comment" className="flex-1" data-testid="pm-comment-input" />
        <IconButton icon={Send} label="Send comment" size="sm" onClick={send} data-testid="pm-comment-send" />
      </div>
    </div>
  );
}

function Attachments({ taskId, canEdit }) {
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-attachments", taskId], queryFn: () => api.pmAttachments(taskId) });

  const addLink = async () => {
    if (!url.trim()) return;
    try { await api.pmAddLink(taskId, url.trim(), ""); setUrl(""); refetch(); toast.success("Link added."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true); setPct(0);
    try { await api.pmUploadFile(taskId, file, setPct); refetch(); toast.success("File uploaded."); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
    finally { setUploading(false); setPct(0); }
  };
  const download = async (a) => {
    try { const blob = await api.pmDownloadAttachment(a.id); downloadBlob(blob, a.original_filename || "file"); }
    catch (e) {
      if (e.response?.status === 410) toast.error("This file was uploaded before the move to Railway and is no longer available.");
      else toast.error(fmtErr(e.response?.data?.detail) || e.message);
    }
  };
  const del = async (a) => { try { await api.pmDeleteAttachment(a.id); refetch(); } catch (e) { toast.error(e.message); } };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-2)", paddingTop: "var(--space-2)", borderTop: "1px solid var(--border-hairline)" }} data-testid={`pm-attachments-${taskId}`}>
      {rows.length === 0 && <span className="sn-small sn-faint">No attachments yet.</span>}
      {rows.map((a) => (
        <div key={a.id} className="sn-row sn-small" style={{ flexWrap: "nowrap", gap: "var(--space-1)" }} data-testid={`pm-attachment-${a.id}`}>
          {a.kind === "link" ? (
            <>
              <Icon name={Link2} size={14} style={{ color: "var(--text-tertiary)", flex: "none" }} />
              <a href={a.url} target="_blank" rel="noreferrer" className="sn-link" style={{ fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block" }}>{a.label || a.url}</a>
            </>
          ) : (
            <>
              <Icon name="FileText" size={14} style={{ color: "var(--text-tertiary)", flex: "none" }} />
              <button type="button" onClick={() => download(a)} className="sn-link" style={{ fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-block", textAlign: "left" }}
                data-testid={`pm-attachment-download-${a.id}`}>{a.original_filename}</button>
              <span className="sn-num sn-faint" style={{ fontSize: 12 }}>{a.size != null ? `(${a.size < 1024 ? "<1" : Math.round(a.size / 1024)} KB)` : ""}</span>
            </>
          )}
          {canEdit && <IconButton icon="X" label={`Remove ${a.label || a.original_filename || "attachment"}`} size="sm" style={{ marginLeft: "auto" }} onClick={() => del(a)} data-testid={`pm-attachment-delete-${a.id}`} />}
        </div>
      ))}
      {canEdit && (
        <div className="sn-stack" style={{ gap: "var(--space-1)" }}>
          <div className="sn-row" style={{ flexWrap: "nowrap", gap: "var(--space-1)" }}>
            <TextField value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLink()}
              placeholder="Paste a link…" aria-label="Link URL" className="flex-1" data-testid="pm-attachment-link-input" />
            <IconButton icon={Link2} label="Add link" size="sm" onClick={addLink} data-testid="pm-attachment-link-add" />
          </div>
          <label className="sn-btn sn-btn-ghost sn-btn-sm" style={{ alignSelf: "flex-start", cursor: uploading ? "default" : "pointer" }} data-testid="pm-attachment-file-label">
            <Icon name={uploading ? "Loader" : Upload} className={uploading ? "sn-spin" : undefined} />
            {uploading ? <span>Uploading… <span className="sn-num">{pct}%</span></span> : "Upload a file (≤15 MB)"}
            <input type="file" className="hidden" onChange={onFile} disabled={uploading} data-testid="pm-attachment-file-input" />
          </label>
        </div>
      )}
    </div>
  );
}

function TaskCard({ t, canEdit, onChanged, meta }) {
  const [editing, setEditing] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [showAttachments, setShowAttachments] = useState(false);
  const [f, setF] = useState({ title: t.title, description: t.description || "", assignee: t.assignee || "", due_date: t.due_date || "", decision_id: t.decision_id || "", prompt_id: t.prompt_id || "" });

  const save = async (patch, msg) => {
    try { await api.pmUpdateTask(t.id, patch); onChanged(); if (msg) toast.success(msg); } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const del = async () => { try { await api.pmDeleteTask(t.id); onChanged(); toast.success("Task deleted."); } catch (e) { toast.error(e.message); } };

  const dec = meta.decisions.find((d) => d.id === t.decision_id);
  const pr = meta.prompts.find((p) => p.id === t.prompt_id);
  const overdue = t.due_date && t.status !== "Done" && t.due_date < new Date().toISOString().slice(0, 10);
  const memberOpts = [{ value: "", label: "Unassigned" }, ...meta.members.map((m) => ({ value: m.email, label: m.name || m.email }))];

  // KanbanCard renders a <button>, which cannot hold this card's own controls, so the card composes its classes.
  return (
    <div className="sn-kcard" style={{ cursor: canEdit && !editing ? "grab" : "default" }}
      data-testid={`pm-card-${t.id}`} draggable={canEdit && !editing} onDragStart={(e) => e.dataTransfer.setData("text/task", t.id)}>
      {editing ? (
        <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
          <TextField label="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="pm-edit-title" />
          <TextArea label="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} data-testid="pm-edit-desc" />
          <Select label="Assignee" options={memberOpts} value={f.assignee} onChange={(v) => setF({ ...f, assignee: v })} data-testid="pm-edit-assignee" />
          <TextField label="Due" type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="pm-edit-due" />
          <Select label="Decision" value={f.decision_id} onChange={(v) => setF({ ...f, decision_id: v })} data-testid="pm-edit-decision"
            options={[{ value: "", label: "No decision" }, ...meta.decisions.map((d) => ({ value: d.id, label: d.title.slice(0, 30) }))]} />
          <Select label="Prompt" value={f.prompt_id} onChange={(v) => setF({ ...f, prompt_id: v })} data-testid="pm-edit-prompt"
            options={[{ value: "", label: "No prompt" }, ...meta.prompts.map((p) => ({ value: p.id, label: p.title.slice(0, 30) }))]} />
          <div className="sn-row">
            <Button variant="primary" size="sm" icon="Check" onClick={() => { save(f, "Task updated."); setEditing(false); }} data-testid="pm-edit-save">Save</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <>
          <span className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap", alignItems: "flex-start" }}>
            <span className="sn-kcard-t" style={{ flex: 1, minWidth: 0 }}>{t.title}</span>
            <span className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap", flex: "none" }}>
              <span className="sn-prio" data-level={PRIO_LEVEL[t.priority] || 1} aria-hidden="true"><i /><i /><i /></span>
              <Badge title={`Priority ${t.priority}`}>{t.priority}</Badge>
            </span>
          </span>
          {t.description && <span className="sn-small sn-muted">{t.description}</span>}
          {(t.assignee || t.due_date || dec || pr) && (
            <span className="sn-kcard-f">
              {t.assignee && (
                <span className="sn-row" style={{ gap: "var(--space-1)" }} data-testid="pm-card-assignee">
                  <Avatar name={t.assignee} size="sm" /> {t.assignee.split("@")[0]}
                </span>
              )}
              {t.due_date && (
                <span className="sn-row sn-num" style={{ gap: "var(--space-1)", color: overdue ? "var(--text-primary)" : undefined, fontWeight: overdue ? 500 : undefined }} data-testid="pm-card-due">
                  <Icon name={overdue ? "TriangleAlert" : "Calendar"} size={12} /> {t.due_date}{overdue ? " (overdue)" : ""}
                </span>
              )}
              {dec && <span className="sn-row" style={{ gap: "var(--space-1)" }} title={dec.title}><Icon name={GitBranch} size={12} /> {dec.title.slice(0, 18)}</span>}
              {pr && <span className="sn-row" style={{ gap: "var(--space-1)" }} title={pr.title}><Icon name="MessageSquare" size={12} /> <span className="sn-num">{pr.ref}</span></span>}
            </span>
          )}
          <span className="sn-row" style={{ gap: "var(--space-1)" }}>
            {canEdit && (
              <>
                <Select options={opts(STATUSES)} value={t.status} onChange={(v) => save({ status: v }, `Moved to ${v}.`)} width={132} aria-label="Status" data-testid="pm-card-status" />
                <IconButton icon="Pencil" label="Edit task" size="sm" onClick={() => setEditing(true)} data-testid="pm-card-edit" />
                <IconButton icon={Trash2} label="Delete task" size="sm" onClick={del} data-testid="pm-card-delete" />
              </>
            )}
            <IconButton icon="MessageSquare" label="Comments" size="sm" aria-pressed={showComments} onClick={() => setShowComments((v) => !v)}
              style={showComments ? { color: "var(--workspace-accent-fg)", background: "var(--workspace-accent-dim)" } : undefined} data-testid="pm-card-comments-toggle" />
            <IconButton icon="Paperclip" label="Attachments" size="sm" aria-pressed={showAttachments} onClick={() => setShowAttachments((v) => !v)}
              style={showAttachments ? { color: "var(--workspace-accent-fg)", background: "var(--workspace-accent-dim)" } : undefined} data-testid="pm-card-attachments-toggle" />
          </span>
          {showComments && <Comments taskId={t.id} />}
          {showAttachments && <Attachments taskId={t.id} canEdit={canEdit} />}
        </>
      )}
    </div>
  );
}

const EMPTY_FORM = { title: "", status: "Backlog", priority: "P1", description: "", assignee: "", due_date: "" };

export default function Board() {
  const { can, user } = useAuth();
  const canEdit = can("analyst");
  const qc = useQueryClient();
  const { mobile } = useViewport();
  const [adding, setAdding] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const [mineOnly, setMineOnly] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: tasks = [], refetch } = useQuery({ queryKey: ["pm-tasks"], queryFn: api.pmTasks });
  const { data: summary } = useQuery({ queryKey: ["pm-summary"], queryFn: api.pmSummary });
  const { data: members = [] } = useQuery({ queryKey: ["pm-members"], queryFn: api.pmMembers });
  const { data: decisions = [] } = useQuery({ queryKey: ["pm-decisions"], queryFn: api.pmDecisions });
  const { data: prompts = [] } = useQuery({ queryKey: ["pm-prompts"], queryFn: api.pmPrompts });
  const meta = { members, decisions, prompts };

  const onChanged = () => { refetch(); qc.invalidateQueries({ queryKey: ["pm-summary"] }); };

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required."); return; }
    try { await api.pmCreateTask(form); setForm(EMPTY_FORM); setAdding(false); onChanged(); toast.success("Task created."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  const onDrop = async (e, status) => {
    e.preventDefault(); setDragCol(null);
    const id = e.dataTransfer.getData("text/task");
    const t = tasks.find((x) => x.id === id);
    if (!t || t.status === status) return;
    try { await api.pmUpdateTask(id, { status }); onChanged(); toast.success(`Moved to ${status}.`); }
    catch (err) { toast.error(fmtErr(err.response?.data?.detail) || err.message); }
  };

  const mine = tasks.filter((t) => t.assignee === user?.email);
  const visible = mineOnly ? mine : tasks;
  const latest = [...decisions].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))).slice(0, 2);

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-board">
      <PageHead
        crumb={["Project", "Board"]}
        title="Task board"
        context={summary
          ? <span className="sn-sans-num">{summary.done} done · {summary.open} open · {summary.total} total · {summary.pending_decisions} decisions pending</span>
          : "Loading summary…"}
        actions={
          <>
            <Button icon="Download" onClick={async () => downloadBlob(await api.pmExport("tasks"), "sentinel_tasks.csv")} data-testid="pm-export-tasks">CSV</Button>
            {canEdit && <Button variant="primary" icon="Plus" aria-expanded={adding} onClick={() => setAdding((v) => !v)} data-testid="pm-new-task-btn">New task</Button>}
          </>
        }
      />

      <Tabs label="Filter tasks" value={mineOnly ? "mine" : "all"} onChange={(v) => setMineOnly(v === "mine")}
        tabs={[
          { value: "all", label: "All", count: tasks.length },
          { value: "mine", label: "Assigned to me", count: mine.length, testId: "pm-filter-mine" },
        ]} />

      {adding && (
        <Panel title="New task" icon="Plus" data-testid="pm-new-task-form">
          <div className="sn-stack">
            <TextField label="Title" placeholder="Task title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="pm-form-title" />
            <TextArea label="Description" placeholder="Optional" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="pm-form-desc" />
            <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
              <Select label="Status" options={opts(STATUSES)} value={form.status} onChange={(v) => setForm({ ...form, status: v })} width={150} data-testid="pm-form-status" />
              <Select label="Priority" options={opts(PRIORITIES)} value={form.priority} onChange={(v) => setForm({ ...form, priority: v })} width={96} data-testid="pm-form-priority" />
              <Select label="Assignee" value={form.assignee} onChange={(v) => setForm({ ...form, assignee: v })} width={200} data-testid="pm-form-assignee"
                options={[{ value: "", label: "Unassigned" }, ...members.map((m) => ({ value: m.email, label: m.name || m.email }))]} />
              <TextField label="Due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="pm-form-due" />
              <Button variant="primary" onClick={create} data-testid="pm-form-create">Create</Button>
            </div>
          </div>
        </Panel>
      )}

      <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
        {canEdit && <span className="sn-help">Drag a card between columns to change its status.</span>}
        <div className="sn-board" role="list" aria-label="Task board"
          style={{ gridAutoFlow: "row", gridTemplateColumns: `repeat(auto-fit, minmax(${mobile ? 240 : 220}px, 1fr))` }}>
            {STATUSES.map((s) => {
              const col = visible.filter((t) => t.status === s);
              const over = dragCol === s;
              return (
                <div key={s} className="sn-col" role="listitem" aria-label={`${s}, ${col.length} tasks`}
                  style={over ? { borderColor: "var(--workspace-accent)", background: "var(--workspace-accent-dim)" } : undefined}
                  data-testid={`pm-col-${s.replace(/\s+/g, "-").toLowerCase()}`}
                  onDragOver={(e) => { if (canEdit) { e.preventDefault(); setDragCol(s); } }} onDragLeave={() => setDragCol(null)} onDrop={(e) => onDrop(e, s)}>
                  <div className="sn-col-h">
                    <Icon name={STATUS_ICON[s]} style={{ color: "var(--text-tertiary)" }} />{s}<span className="sn-num">{col.length}</span>
                  </div>
                  <div className="sn-col-b" style={{ minHeight: 96 }}>
                    {col.length === 0 && <span className="sn-small sn-faint" style={{ padding: "var(--space-4) 0", textAlign: "center" }}>No tasks</span>}
                    {col.map((t) => <TaskCard key={t.id} t={t} canEdit={canEdit} onChanged={onChanged} meta={meta} />)}
                  </div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="sn-grid-12">
        <Panel className="sn-col-7" title="Decisions log · latest" icon="Gavel" flush
          actions={<Link to="/pm/decisions" className="sn-link" style={{ fontSize: 12 }}>All decisions<Icon name="ArrowRight" size={14} /></Link>}>
          {latest.length === 0 ? (
            <div style={{ padding: "var(--space-4)" }}><span className="sn-small sn-faint">No decisions recorded yet.</span></div>
          ) : latest.map((d) => (
            <DecisionLogEntry key={d.id} title={d.title} status={d.status} date={d.created_at ? String(d.created_at).slice(0, 10) : ""} author={d.proposed_by}>
              {d.detail || "No detail recorded."}
            </DecisionLogEntry>
          ))}
        </Panel>
        <Panel className="sn-col-5" title="Summary" icon="ListChecks">
          {summary ? (
            <div className="sn-row" style={{ gap: "var(--space-6)" }}>
              <StatCell size="md" label="Open" value={String(summary.open)} />
              <StatCell size="md" label="Done" value={String(summary.done)} sub={`of ${summary.total} total`} />
              <StatCell size="md" label="Decisions pending" value={String(summary.pending_decisions)} />
            </div>
          ) : <Skeleton lines={2} />}
        </Panel>
      </div>
    </div>
  );
}
